import { useAuth } from "common/hooks/useAuth";

import { useEffect, useId, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import BlockFormContainer, {
  BlockFormFieldsContainer,
  BlockFormTitleContainer,
} from "layouts/BlockFormLayout";

import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Breadcrumbs } from "@mui/material";
import axios from "axios";
import CustomBreadcrumb from "common/components/CustomBreadcrumb";
import LoadingSpinner from "common/components/LoadingSpinner";
import MasksFileForm from "common/containers/Forms/UploadFile/MasksFileForm";
import { BACKEND_URL_CONFIG } from "config";
import { ERRORS } from "errors";
import MainLayout from "layouts/MainLayout";
import { Fragment } from "react";
import { toast } from "react-toastify";
import styles from "./EditMasksFilePage.module.css";

export default function EditMasksFilePage() {
  // Contexts
  const { currentUser } = useAuth();
  const { masksFileId } = useParams();
  const navigate = useNavigate();

  // States
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [masksFileInformation, setMasksFileInformation] = useState(null);

  // Refs
  const formIdRef = useId();

  useEffect(() => {
    const controller = new AbortController();
    fetchMasksFileInformation(controller);
    return () => controller.abort();
  }, []);

  const fetchMasksFileInformation = async (controller) => {
    setLoading(true);
    const url = BACKEND_URL_CONFIG.getMaskFileInformation.replace(
      "{maskFileId}",
      masksFileId
    );
    try {
      const response = await axios.get(url, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
        signal: controller.signal,
      });
      if (response.status === 200) {
        setMasksFileInformation(response.data);
      }
    } catch (error) {
      if (error.response.status === 404) {
        navigate("/masks");
        toast.error(ERRORS.error_182);
        return;
      }
      toast.error(ERRORS.error_168);
    } finally {
      setLoading(false);
    }
  };

  const saveMasksFile = async (controller) => {
    const payload = {
      name: formData.file_label,
      is_certified: formData.is_certified,
    };
    updateMasksFile(payload, controller);
  };

  const updateMasksFile = async (payload, controller) => {
    setLoading(true);
    try {
      const url = BACKEND_URL_CONFIG.updateMasksFile.replace(
        "{maskFileId}",
        masksFileId
      );
      const response = await axios.put(url, payload, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
        signal: controller.signal,
      });
      if (response.status === 200) {
        toast.success("Votre fichier de masques a été modifié");
        navigate("/masks");
      }
    } catch (error) {
      if (error.response.status === 409) {
        toast.error(ERRORS.error_171);
      } else {
        toast.error(ERRORS.error_167);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    if (formData) {
      saveMasksFile(controller);
    }
    return () => controller.abort();
  }, [formData]);

  return (
    <Fragment>
      <MainLayout>
        <div className={styles.container}>
          <div className={styles["breadcrumbs-wrapper"]}>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <CustomBreadcrumb
                component="a"
                href="/"
                label="Page d'accueil"
                icon={<FontAwesomeIcon icon={faHome} />}
              />
              <CustomBreadcrumb component="a" href="/masks" label="Masques" />
              <CustomBreadcrumb component="a" href="#" label="Modifier" />
            </Breadcrumbs>
          </div>
          <div className={styles["main-section"]}>
            <BlockFormContainer>
              <BlockFormTitleContainer
                title="Modifier un fichier de masques"
                formId={formIdRef}
                loading={loading}
                isSubmitting={isSubmitting}
              />
              <BlockFormFieldsContainer>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <MasksFileForm
                    masksFileInformation={masksFileInformation}
                    setFormData={setFormData}
                    formId={formIdRef}
                    setIsSubmitting={setIsSubmitting}
                  />
                )}
              </BlockFormFieldsContainer>
            </BlockFormContainer>
          </div>
        </div>
      </MainLayout>
    </Fragment>
  );
}
